import { useState } from 'react';
import { Languages, RotateCcw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useTemplateDesigner } from '../TemplateDesignerContext';
import { DEFAULT_TEMPLATE_SETTINGS } from '../types';

const LABEL_CATEGORIES = [
  { id: 'documentHeadings', name: 'Document Headings', description: 'Titles and header labels' },
  { id: 'general', name: 'General', description: 'Table and line-item fields' },
  { id: 'documentSummary', name: 'Summary', description: 'Totals and signatures' },
  { id: 'remittance', name: 'Remittance', description: 'Payment section labels' },
] as const;

// Human-readable display names for camelCase keys
const LABEL_DISPLAY_NAMES: Record<string, string> = {
  // Document Headings
  Invoice: 'Invoice',
  Quote: 'Quote',
  CreditMemo: 'Credit Memo',
  PurchaseOrder: 'Purchase Order',
  Receipt: 'Receipt',
  // General
  BillTo: 'Bill To',
  ShipTo: 'Ship To',
  Vendor: 'Vendor',
  Date: 'Date',
  Due: 'Due Date',
  ValidUntil: 'Valid Until',
  Terms: 'Terms',
  Notes: 'Notes',
  Item: 'Item',
  Code: 'Code',
  Description: 'Description',
  Quantity: 'Quantity',
  Rate: 'Rate',
  Tax: 'Tax',
  Amount: 'Amount',
  // Document Summary
  subtotal: 'Subtotal',
  tax: 'Tax',
  discount: 'Discount',
  markup: 'Markup',
  total: 'Total',
  paid: 'Amount Paid',
  balance: 'Balance Due',
  deposit: 'Deposit Due',
  // Remittance
  BalanceDue: 'Balance Due',
  QuoteTotal: 'Quote Total',
  CreditAmount: 'Credit Amount',
  AmountPaid: 'Amount Paid',
};

// Get display name for a key, fallback to adding spaces before capitals
const getDisplayName = (key: string): string => {
  if (LABEL_DISPLAY_NAMES[key]) return LABEL_DISPLAY_NAMES[key];
  // Fallback: convert camelCase to "Camel Case"
  return key.replace(/([A-Z])/g, ' $1').trim();
};

export function LabelsPanel() {
  const { settings, updateSettings } = useTemplateDesigner();
  const { labels } = settings;
  const [activeCategory, setActiveCategory] = useState<string>('documentHeadings');
  const [searchQuery, setSearchQuery] = useState('');

  const currentLabels = labels[activeCategory as keyof typeof labels] as Record<string, string>;
  const defaultLabels = DEFAULT_TEMPLATE_SETTINGS.labels[activeCategory as keyof typeof labels] as Record<string, string>;

  const updateLabel = (key: string, value: string) => {
    updateSettings('labels', {
      ...labels,
      [activeCategory]: {
        ...currentLabels,
        [key]: value,
      },
    });
  };

  const resetCategory = () => {
    updateSettings('labels', {
      ...labels,
      [activeCategory]: defaultLabels,
    });
  };

  const filteredLabels = Object.entries(currentLabels).filter(([key]) =>
    getDisplayName(key).toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-primary/10 to-primary/5">
        <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
          <Languages className="w-5 h-5 text-primary" />
        </div>
        <div>
          <Label className="text-sm font-semibold">Custom Labels</Label>
          <p className="text-xs text-muted-foreground">
            Customize all text throughout your documents
          </p>
        </div>
      </div>

      {/* Category Selector */}
      <div className="flex gap-1.5 flex-wrap">
        {LABEL_CATEGORIES.map((category) => (
          <button
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={`
              px-3 py-1.5 text-xs font-medium rounded-full border transition-all
              ${activeCategory === category.id
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border hover:border-primary/50 hover:bg-accent'
              }
            `}
          >
            {category.name}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search labels..."
          className="pl-10 bg-white"
        />
      </div>

      {/* Reset Button */}
      <div className="flex justify-end">
        <Button variant="ghost" size="sm" onClick={resetCategory}>
          <RotateCcw className="w-3 h-3 mr-1.5" />
          Reset {LABEL_CATEGORIES.find(c => c.id === activeCategory)?.name}
        </Button>
      </div>

      {/* Labels List */}
      <div className="space-y-2">
        {filteredLabels.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            No labels match your search
          </div>
        ) : (
          filteredLabels.map(([key, value]) => (
            <div
              key={key}
              className="grid grid-cols-2 gap-3 items-center p-2 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div>
                <span className="text-sm font-medium">{getDisplayName(key)}</span>
                {value !== defaultLabels[key] && (
                  <span className="ml-2 text-[10px] text-primary font-medium">Modified</span>
                )}
              </div>
              <Input
                value={value}
                onChange={(e) => updateLabel(key, e.target.value)}
                className="h-8 text-sm"
              />
            </div>
          ))
        )}
      </div>

      {/* Info */}
      <div className="p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
        <p>
          <strong>Tip:</strong> Use <code className="bg-muted px-1 py-0.5 rounded">%~</code> as a
          placeholder for dynamic values like percentages or dates.
        </p>
      </div>
    </div>
  );
}
